import { isToolUIPart, type UIMessage } from "ai";
import { useEffect, useMemo, useRef } from "react";

import {
  findCreateBookingDetailsForId,
  formatConfirmationDateTime,
  parseResolvedCreateBooking,
} from "./chat-message-utils";

export type SetMessagesFn = (
  updater: UIMessage[] | ((messages: UIMessage[]) => UIMessage[])
) => void;

/**
 * Polls public booking payment status and appends a confirmation assistant message when paid
 * (same behaviour as the standalone chat page).
 */
export function usePaymentConfirmationPoll(
  backendApiBase: string,
  messages: UIMessage[],
  setMessages: SetMessagesFn,
  fetchCredentials: RequestCredentials = "omit"
): void {
  const paymentPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const confirmedPaidBookingIdsRef = useRef<Set<string>>(new Set());
  const messagesRef = useRef<UIMessage[]>([]);
  messagesRef.current = messages;

  const toolInvocations = useMemo(
    () =>
      messages.flatMap((message) =>
        message.role === "assistant" ? message.parts.filter((part) => isToolUIPart(part)) : []
      ),
    [messages]
  );

  const pendingPaymentBookingId = useMemo(() => {
    let lastId: string | null = null;
    for (const part of toolInvocations) {
      const parsed = parseResolvedCreateBooking(part);
      if (parsed) lastId = parsed.bookingId;
    }
    if (!lastId || confirmedPaidBookingIdsRef.current.has(lastId)) return null;
    return lastId;
  }, [toolInvocations]);

  useEffect(() => {
    function clearPaymentPoll() {
      if (paymentPollIntervalRef.current !== null) {
        clearInterval(paymentPollIntervalRef.current);
        paymentPollIntervalRef.current = null;
      }
    }

    if (!pendingPaymentBookingId) {
      clearPaymentPoll();
      return clearPaymentPoll;
    }

    const bookingId = pendingPaymentBookingId;
    const base = backendApiBase.replace(/\/$/, "");
    const statusUrl = `${base}/bookings/${encodeURIComponent(bookingId)}/status`;

    const tick = async () => {
      if (confirmedPaidBookingIdsRef.current.has(bookingId)) {
        clearPaymentPoll();
        return;
      }
      try {
        const res = await fetch(statusUrl, { credentials: fetchCredentials });
        if (!res.ok) return;
        const data = (await res.json()) as { paymentStatus?: string };
        if (data.paymentStatus !== "paid") return;
        if (confirmedPaidBookingIdsRef.current.has(bookingId)) return;

        confirmedPaidBookingIdsRef.current.add(bookingId);
        clearPaymentPoll();

        const { location, date, time } = findCreateBookingDetailsForId(messagesRef.current, bookingId);
        const locDisplay = location.trim() || "the venue";
        const whenText = formatConfirmationDateTime(date, time);
        const body = `Your booking is confirmed and paid. See you at ${locDisplay} on ${whenText}. Your booking reference is ${bookingId}.`;

        setMessages((prev) => [
          ...prev,
          {
            id: `payment-confirmed-${bookingId}-${crypto.randomUUID()}`,
            role: "assistant",
            parts: [{ type: "text", text: body, state: "done" }],
          },
        ]);
      } catch {
        /* ignore transient network errors */
      }
    };

    void tick();
    paymentPollIntervalRef.current = setInterval(() => {
      void tick();
    }, 3000);

    return clearPaymentPoll;
  }, [pendingPaymentBookingId, setMessages, backendApiBase, fetchCredentials]);
}
